<template>
    <div class="overflow-hidden relative h-screen text-white">
        <template v-for="(item, i) in items" :key="i">
            <video
                v-if="item.video"
                :poster="item.src"
                autoplay
                muted
                loop
                class="object-cover object-center top-0 left-0 w-full h-full transition-all duration-1000 ease-out transform origin-center"
                :class="[i === index ? 'block' : 'hidden']">
                <source :src="item.video" :type="`video/${item.videoType}`" />
            </video>
            <ResponsiveImage
                v-else
                show-wide
                :urls="item.urls"
                alt-title=""
                :class="[i === index ? 'opacity-100 scale-100' : 'opacity-0 scale-150']"
                class="object-cover object-center absolute top-0 left-0 w-full h-full transition-all duration-1000 ease-out transform origin-center" />
        </template>
        <div class="absolute bottom-0 w-full bg-gradient-to-t from-black-900">
            <div class="container">
                <h1
                    v-if="!!currentItem && !!currentItem.title"
                    class="text-stage-title-mobile md:text-stage-title-tablet xl:text-stage-title-desktop mb-2 md:mb-12 xl:mb-16">
                    {{ currentItem.title }}
                </h1>
                <ul class="pb-24 md:pb-15 xl:pb-10 xl:inline-grid xl:gap-x-16 xl:grid-cols-3 w-full">
                    <li
                        v-for="(item, i) in items"
                        :key="`${item.title}-${i}`"
                        :class="[i === index ? 'inline-flex' : 'hidden']"
                        class="overflow-hidden flex-col xl:inline-flex xl:max-w-sm xl:h-30 md:flex-row">
                        <div class="flex flex-col justify-between items-start md:order-2">
                            <div class="hidden text-para-lg md:block">{{ item.description }}</div>
                            <a
                                v-if="item.link"
                                :href="item.link"
                                :target="item.target"
                                :aria-label="item.ariaLabel ? item.ariaLabel : item.title"
                                class="inline-flex justify-between items-center text-para-s font-semibold group">
                                {{ item.label }}
                                <ArrowButton direction="right" class="ml-2 w-10 h-10" />
                            </a>
                        </div>
                        <LoadSpinner v-if="items.length > 1" class="mr-4 md:order-1" :active="index === i" />
                    </li>
                </ul>
            </div>
        </div>
        <div
            v-if="items.length > 1"
            class="flex absolute bottom-0 justify-center items-center py-6 md:py-10 space-x-2 w-full xl:hidden">
            <span
                v-for="i in items.length"
                :key="`stage-page-${i}`"
                class="inline-black w-1.5 h-1.5 rounded-full transition-colors bg-black-300"
                :class="{ 'bg-white': index === i - 1 }" />
        </div>
        <div class="absolute bottom-2 md:bottom-6 xl:bottom-10 right-4 flex">
            <button type="button" :aria-label="$globalLabels.previous" @click="previous" class="group hidden xl:flex">
                <ArrowButton direction="left" class="w-10 h-10"></ArrowButton>
            </button>
            <button v-if="playing" type="button" :aria-label="$globalLabels.pause" @click="pause" class="group flex">
                <ArrowButton direction="pause" class="w-10 h-10"></ArrowButton>
            </button>
            <button v-else type="button" :aria-label="$globalLabels.play" @click="start" class="group flex">
                <ArrowButton direction="play" class="w-10 h-10"></ArrowButton>
            </button>
            <button type="button" :aria-label="$globalLabels.next" @click="next" class="group hidden xl:flex">
                <ArrowButton direction="right" class="w-10 h-10"></ArrowButton>
            </button>
        </div>
        <slot />
    </div>
</template>

<script lang="ts">
import LoadSpinner from './LoadSpinner.vue';
import ResponsiveImage from '../base/ResponsiveImage.vue';
import ArrowButton from '../base/ArrowButton.vue';

export default {
    components: { ArrowButton, ResponsiveImage, LoadSpinner },
    provide() {
        return {
            registerCarouselItem: this.register,
        };
    },
    inject: ['$globalLabels'],
    data() {
        return {
            items: [],
            index: 0,
            interval: null,
            playing: false,
            wasPlaying: false,
        };
    },
    computed: {
        currentItem() {
            if (this.items.length > this.index) {
                return this.items[this.index];
            }
            return null;
        },
    },
    mounted(): void {
        this.$el.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.next();
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.previous();
            }
        });
        this.$el.addEventListener('pointerdown', (event: PointerEvent) => {
            this.wasPlaying = this.playing;
            if (!this.targetInteractive(event)) {
                this.pause();
            }
        });
        this.$el.addEventListener('pointerup', (event: PointerEvent) => {
            if (!this.targetInteractive(event)) {
                this.next();
                if (this.wasPlaying) {
                    this.start();
                }
            }
        });
    },
    beforeUnmount(): void {
        this.pause();
    },
    methods: {
        /**
         * This method is called by child items.
         *
         * @param item A child item that wants to register itself
         */
        register(item: any) {
            // in this case only, we have to copy all properties (to prevent issues with reactivity)
            this.items.push(Object.assign({}, item));

            // cannot determine tag of child nodes in Vue3
            // instead check for a specific property that should be present in the children we want to count
            if (this.$slots.default().filter(child => child.props.urls).length === this.items.length) {
                this.start();

                // pause if user prefers reduced motion
                if (window.matchMedia(`(prefers-reduced-motion: reduce)`).matches) {
                    this.pause();
                }
            }
        },
        start() {
            this.interval = setInterval(() => {
                this.cycleIndex();
            }, 4500);
            this.playing = true;
        },
        pause() {
            clearInterval(this.interval);
            this.playing = false;
        },
        next() {
            this.pause();
            this.cycleIndex();
        },
        previous() {
            this.pause();
            this.cycleIndexReverse();
        },
        cycleIndex() {
            if (this.index + 1 < this.items.length) {
                this.index++;
            } else {
                this.index = 0;
            }
        },
        cycleIndexReverse() {
            if (this.index - 1 >= 0) {
                this.index--;
            } else {
                this.index = this.items.length - 1;
            }
        },
        targetInteractive(event: MouseEvent | PointerEvent) {
            const target = event.target as HTMLElement;
            return !!target.closest('button, a, input, textarea, select, [role="button"]');
        },
    },
};
</script>
